import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { OrderPeriodFilter } from './OrderPeriodFilter';
import { OrdersByTimeChart } from './OrdersByTimeChart';
import {
  buildOrdersAnalyticsParams,
  computeAnalyticsTotals,
  defaultBucketForPeriod,
  formatInr,
  formatPeriodRange,
} from './dashboardPeriodUtils';

function KpiTile({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200/80 bg-slate-50/80 px-3 py-2.5 min-w-[120px]">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-lg font-bold text-slate-900 tabular-nums mt-0.5">{value}</p>
    </div>
  );
}

/**
 * Dashboard footer: order trend chart with its own period filter.
 */
export function OrdersAnalyticsSection({ refreshTick = 0 }) {
  const [period, setPeriod] = useState('30D');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const chartBucket = useMemo(() => defaultBucketForPeriod(period), [period]);

  const loadChart = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (period === 'CUSTOM' && (!customFrom || !customTo)) return;

    setLoading(true);
    setError('');
    try {
      const qs = buildOrdersAnalyticsParams({
        period,
        from: customFrom,
        to: customTo,
        bucket: chartBucket,
      });
      const { data } = await api.get(`${endpoints.dashboard.ordersAnalytics}?${qs}`);
      setAnalytics(data);
    } catch (e) {
      setError(e?.message || 'Failed to load chart');
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [period, chartBucket, customFrom, customTo]);

  useEffect(() => {
    loadChart();
  }, [loadChart, refreshTick]);

  const totals = useMemo(() => computeAnalyticsTotals(analytics), [analytics]);
  const rangeLabel = formatPeriodRange(analytics);

  const bucketHint = useMemo(() => {
    const t = analytics?.bucketType || chartBucket;
    switch (t) {
      case 'hour':
        return 'Time axis: each hour today (12 AM – 11 PM)';
      case 'date':
        return 'Time axis: one bar per day in the selected range';
      case 'month':
        return 'Time axis: one bar per month';
      case 'weekday':
        return 'Time axis: totals by weekday (Mon–Sun) in the range';
      default:
        return '';
    }
  }, [analytics?.bucketType, chartBucket]);

  return (
    <section className="space-y-4 rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-5 shadow-sm">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Orders by time</h2>
          {rangeLabel ? (
            <p className="text-xs text-slate-500 mt-0.5">{rangeLabel}</p>
          ) : null}
          {bucketHint && !loading ? (
            <p className="text-[11px] text-slate-400 mt-0.5">{bucketHint}</p>
          ) : null}
        </div>
        <OrderPeriodFilter
          period={period}
          onPeriodChange={setPeriod}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
        />
      </div>

      {error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2 text-sm">
          {error}
        </div>
      ) : null}

      {!loading && analytics ? (
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <KpiTile label="Orders" value={totals.totalOrders.toLocaleString()} />
          <KpiTile label="Order value" value={formatInr(totals.totalAmount)} />
        </div>
      ) : null}

      <OrdersByTimeChart analytics={analytics} loading={loading} />
    </section>
  );
}

export default OrdersAnalyticsSection;
